import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router';
import { ApiError, Banner, useApiClient, useQuery } from '@acc/web-shared';
import { coverage, LIFE_STATUSES, missedCoverages, missedKindLabel, PC_STATUSES, QUOTE_TYPES } from '@acc/domain';
import { PageHead, dateTime, useAgency } from '../agency';
import { QuoteList, RoutingPill, type Opportunity } from './Quotes';
import { DocumentsPanel } from '../documents';

interface Detail {
  opportunity: Opportunity;
  canEdit: boolean;
  canActivate: boolean;
  events: Array<{ coverage: 'PC' | 'Life'; type: string; ownerMembershipId: string | null; actorMembershipId: string | null; occurredAt: string }>;
  activity: Array<{ authorMembershipId: string | null; body: string; createdAt: string }>;
  assignments: Array<{ membershipId: string; startedAt: string; endedAt: string | null }>;
  reopenRequests: Array<{ requestId: string; status: string; coverages: string; requestedBy: string; requestedAt: string; decidedBy: string | null; decidedAt: string | null }>;
}

type Form = Pick<Opportunity, 'customerName' | 'phone' | 'email' | 'address' | 'currentInsurance' | 'type' | 'pcStatus' | 'lifeStatus' | 'ownerMembershipId' | 'dueDate' | 'renewalDate' | 'policyNumber' | 'finalized' | 'acord' | 'referredBy' | 'needs' | 'notes'>;
const pick = (o: Opportunity): Form => ({ customerName: o.customerName, phone: o.phone, email: o.email, address: o.address, currentInsurance: o.currentInsurance, type: o.type, pcStatus: o.pcStatus, lifeStatus: o.lifeStatus, ownerMembershipId: o.ownerMembershipId, dueDate: o.dueDate, renewalDate: o.renewalDate, policyNumber: o.policyNumber, finalized: o.finalized, acord: o.acord, referredBy: o.referredBy, needs: o.needs, notes: o.notes });

export function QuoteDetail() {
  const a = useAgency();
  const { opportunityId = '' } = useParams();
  const api = useApiClient();
  const { refreshCounts } = useOutletContext<{ refreshCounts: () => void }>();
  const nav = useNavigate();
  const q = useQuery<Detail>(a.path(`/opportunities/${opportunityId}`));
  const [form, setForm] = useState<Form | null>(null);
  const [note, setNote] = useState('');
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const mentions = useQuery<Array<{ mentionId: string; opportunityId: string; addressedAt: string | null; authorMembershipId: string; createdAt: string }>>(a.path('/mentions'));
  const activeStaff = a.team.filter((m) => m.status === 'Active');
  const noteMentions = mentions.data?.filter((m) => m.opportunityId === opportunityId && !m.addressedAt) ?? [];
  const [tab, setTab] = useState<'details' | 'documents' | 'activity'>('details');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const savingRef = useRef(false);
  // A successful PATCH advances the server version before the query reload finishes.
  const latestVersionRef = useRef<number | null>(null);
  const baselineRef = useRef<Form | null>(null);

  useEffect(() => {
    if (!q.data) return;
    const incoming = pick(q.data.opportunity);
    latestVersionRef.current = Math.max(latestVersionRef.current ?? 0, q.data.opportunity.version);
    const previousBaseline = baselineRef.current;
    baselineRef.current = incoming;
    setForm((previous) => {
      // Keep actual local edits, but refresh untouched fields after actions or reloads.
      if (!previous || !previousBaseline) return incoming;
      const next = { ...incoming };
      for (const key of Object.keys(incoming) as (keyof Form)[]) {
        if (previous[key] !== previousBaseline[key]) (next as Record<string, unknown>)[key] = previous[key];
      }
      return next;
    });
  }, [q.data]);

  const hasUnsavedChanges = !!(form && q.data && JSON.stringify(form) !== JSON.stringify(pick(q.data.opportunity)));
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedChanges]);

  const closeQuote = () => {
    if (busy || savingRef.current) return;
    if (hasUnsavedChanges && !window.confirm('Discard unsaved quote changes and close?')) return;
    nav(`${a.base}/quotes`);
  };

  async function act(label: string, fn: () => Promise<unknown>, okText?: string) {
    setBusy(true); setError('');
    try {
      await fn();
      if (okText) a.notify('ok', okText);
      refreshCounts();
      q.reload();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? `${(err as Error).message}. The latest version has been loaded.` : `${label}: ${(err as Error).message}`);
      if (err instanceof ApiError && err.status === 409) q.reload();
    } finally { setBusy(false); }
  }

  useEffect(() => {
    if (!form || !q.data || !q.data.canEdit || savingRef.current || saveStatus === 'error') return;
    if (busy || JSON.stringify(form) === JSON.stringify(pick(q.data.opportunity))) return;
    const snapshot = form;
    const opportunity = q.data.opportunity;
    const timer = setTimeout(() => { void saveChanges(snapshot, opportunity); }, 1500);
    return () => clearTimeout(timer);
  }, [form, q.data, saveStatus, busy]);


  if (q.error) return <Banner tone="bad">{q.error.message === 'not found' ? 'This quote is not available to your account.' : q.error.message}</Banner>;
  if (!q.data || !form) return <p className="muted">Loading…</p>;
  const { opportunity: o, canEdit, canActivate, events, activity, assignments, reopenRequests } = q.data;
  const pending = reopenRequests.find((r) => r.status === 'Pending');
  const missed = missedCoverages(o);
  const set = <K extends keyof Form>(k: K) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value === '' ? null : e.target.value } as Form);
  const dirty = JSON.stringify(form) !== JSON.stringify(pick(o));

  async function saveChanges(snapshot: Form, opportunity: Opportunity, notify = false) {
    if (savingRef.current) return;
    savingRef.current = true;
    setBusy(true);
    setSaveStatus('saving');
    setError('');
    const body: Record<string, unknown> = { version: Math.max(opportunity.version, latestVersionRef.current ?? 0) };
    for (const [key, value] of Object.entries(snapshot)) {
      if (value !== (pick(opportunity) as Record<string, unknown>)[key]) body[key] = value;
    }
    if (!a.manager) delete body.ownerMembershipId;
    try {
      const saved = await api<{ opportunity: Opportunity }>(a.path(`/opportunities/${opportunity.opportunityId}`), { method: 'PATCH', body });
      latestVersionRef.current = saved.opportunity.version;
      setSaveStatus('saved');
      if (notify) a.notify('ok', 'Quote saved.');
      refreshCounts();
      q.reload();
    } catch (err) {
      setSaveStatus('error');
      setError(err instanceof ApiError && err.status === 409
        ? 'Save conflict: another employee changed this quote. Your edits are still on screen; copy them before refreshing.'
        : `Save failed: ${(err as Error).message}. Your edits are still on screen.`);
    } finally {
      savingRef.current = false;
      setBusy(false);
    }
  }

  const save = (e: FormEvent) => {
    e.preventDefault();
    if (form && q.data) { setSaveStatus('idle'); void saveChanges(form, q.data.opportunity, true); }
  };


  return (
    <>
      <div aria-hidden="true" style={{ filter: 'blur(2px)', pointerEvents: 'none', userSelect: 'none' }}><QuoteList /></div>
      <div role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeQuote(); }} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(8, 18, 38, 0.65)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
      <section role="dialog" aria-modal="true" aria-label={`Quote card: ${o.customerName}`} style={{ background: 'var(--surface, #fff)', color: 'var(--text, #17243b)', borderRadius: 14, width: 'min(1120px, 100%)', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto', padding: 24, boxShadow: '0 24px 80px rgba(0,0,0,.35)' }}>
      <div className="crumbs"><Link to={`${a.base}/quotes`}>Quotes</Link> / {o.customerName}</div>
      <PageHead title={o.customerName} sub={<>{o.type} · owner {a.name(o.ownerMembershipId)} · <RoutingPill o={o} /></>} />
      <div className="actions" style={{ justifyContent: "flex-end" }}><button type="button" className="btn" onClick={closeQuote} disabled={busy}>Close quote</button></div>
      {error && <Banner tone="bad">{error}</Banner>}
      <div className="tabs" role="tablist" aria-label="Quote card sections">
        <button type="button" role="tab" aria-selected={tab === 'details'} className={tab === 'details' ? 'on' : ''} onClick={() => setTab('details')}>Customer Details</button>
        <button type="button" role="tab" aria-selected={tab === 'documents'} className={tab === 'documents' ? 'on' : ''} onClick={() => setTab('documents')}>Documents</button>
        <button type="button" role="tab" aria-selected={tab === 'activity'} className={tab === 'activity' ? 'on' : ''} onClick={() => setTab('activity')}>Activity &amp; Messages</button>
      </div>
      {tab === 'details' && <div>

      {a.manager && o.pcStatus === 'Missed' && o.approval === 'Pending' && (
        <section className="card panel-warn" aria-labelledby="approval">
          <h2 id="approval">Missed quote awaiting approval</h2>
          <p className="sub">Approving schedules it for requote follow-up. Rejecting closes it as missed.</p>
          <div className="actions" style={{ justifyContent: 'flex-start' }}>
            <button className="btn primary" disabled={busy} onClick={() => void act('Approval failed', () => api(a.path(`/opportunities/${o.opportunityId}/approval`), { method: 'POST', body: { decision: 'Approved', version: o.version } }), 'Approved for follow-up.')}>Approve for follow-up</button>
            <button className="btn danger" disabled={busy} onClick={() => void act('Approval failed', () => api(a.path(`/opportunities/${o.opportunityId}/approval`), { method: 'POST', body: { decision: 'Rejected', version: o.version } }), 'Missed quote rejected.')}>Reject</button>
          </div>
        </section>
      )}
      {canActivate && (
        <section className="card panel-warn">
          <h2>Ready to requote</h2>
          <p className="sub">Activating moves P/C back to Quoting as a requote, due in 3 days.</p>
          <div className="actions" style={{ justifyContent: 'flex-start' }}><button className="btn primary" disabled={busy} onClick={() => void act('Activate failed', () => api(a.path(`/opportunities/${o.opportunityId}/activate`), { method: 'POST', body: { version: o.version } }), 'Moved back to active quoting.')}>Activate requote</button></div>
        </section>
      )}
      {missed.length > 0 && (
        <section className="card" aria-labelledby="reopen">
          <h2 id="reopen">{missedKindLabel(o)} missed</h2>
          {pending ? (
            <>
              <p className="sub">Early reopening requested by {a.name(pending.requestedBy)} on {dateTime(pending.requestedAt)}.</p>
              {a.manager && (
                <div className="actions" style={{ justifyContent: 'flex-start' }}>
                  <button className="btn primary" disabled={busy} onClick={() => void act('Decision failed', () => api(a.path(`/opportunities/${o.opportunityId}/reopen-request/decision`), { method: 'POST', body: { approved: true } }), 'Reopened for quoting; owner kept.')}>Approve reopening</button>
                  <button className="btn" disabled={busy} onClick={() => void act('Decision failed', () => api(a.path(`/opportunities/${o.opportunityId}/reopen-request/decision`), { method: 'POST', body: { approved: false } }), 'Request declined; the scheduled follow-up remains.')}>Decline</button>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="sub">Ask management to reopen the missed coverage before its scheduled follow-up.</p>
              <div className="actions" style={{ justifyContent: 'flex-start' }}><button className="btn" disabled={busy} onClick={() => void act('Request failed', () => api(a.path(`/opportunities/${o.opportunityId}/reopen-request`), { method: 'POST' }), 'Reopening requested; management will review it.')}>Request early reopening</button></div>
            </>
          )}
        </section>
      )}

      <form className="card" onSubmit={save} aria-labelledby="details">
        <h2 id="details">Opportunity</h2>
        {!canEdit && <Banner tone="warn">{coverage(o).onlyMissed ? 'Read only: missed coverage stays as it is until it is reopened.' : 'Read only: requotes are managed by the agency owner or office manager.'}</Banner>}
        <fieldset disabled={!canEdit || busy} style={{ border: 0, padding: 0, margin: 0 }}>
          <div className="form-grid">
            <label className="field">Customer name<input required maxLength={200} value={form.customerName} onChange={(e) => setForm({ ...form, customerName: e.target.value })} /></label>
            <label className="field">Quote type<select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as Form['type'] })}>{QUOTE_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
            <label className="field">P/C status<select value={form.pcStatus ?? ''} onChange={set('pcStatus')}><option value="">Not quoted</option>{PC_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
            <label className="field">Life status<select value={form.lifeStatus ?? ''} onChange={set('lifeStatus')}><option value="">Not quoted</option>{LIFE_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label>
            <label className="field">Owner
              <select value={form.ownerMembershipId ?? ''} onChange={set('ownerMembershipId')} disabled={!a.manager}>
                <option value="">Unassigned</option>
                {a.team.map((m) => <option key={m.membershipId} value={m.membershipId}>{m.displayName ?? m.email}</option>)}
                {o.ownerMembershipId && !a.team.some((m) => m.membershipId === o.ownerMembershipId) && <option value={o.ownerMembershipId}>Former team member</option>}
              </select>
            </label>
            <label className="field">Phone<input type="tel" value={form.phone ?? ''} onChange={set('phone')} /></label>
            <label className="field">Email<input type="email" value={form.email ?? ''} onChange={set('email')} /></label>
            <label className="field">Referred by<input value={form.referredBy ?? ''} onChange={set('referredBy')} /></label>
            <label className="field full">Notes<textarea value={form.notes ?? ''} onChange={set('notes')} /></label>
            {o.email && <div className="field"><span>Email Customer</span><a className="btn" href={`mailto:${encodeURIComponent(o.email)}?subject=${encodeURIComponent('Your Insurance Quote')}`}>Open email app</a></div>}
          </div>
          <details className="quote-section"><summary>Vehicles &amp; Drivers</summary><p className="muted">Vehicle and driver details will be available when their approved fields are added.</p></details>
          <details className="quote-section"><summary>Property</summary><div className="form-grid">
            <label className="field full">Address<input value={form.address ?? ''} onChange={set('address')} /></label>
          </div></details>
          <details className="quote-section"><summary>Coverage</summary><div className="form-grid">
            <label className="field">Current insurance<input value={form.currentInsurance ?? ''} onChange={set('currentInsurance')} /></label>
            <label className="field">Policy number<input maxLength={60} value={form.policyNumber ?? ''} onChange={set('policyNumber')} /></label>
            <label className="field">Finalized<select value={form.finalized ?? ''} onChange={set('finalized')}><option value="">Not selected</option><option value="Thank You">Thank You</option><option value="Life Thank You">Life Thank You</option><option value="Gift Box">Gift Box</option></select></label>
            <label className="field">ACORD<select value={form.acord ?? ''} onChange={set('acord')}><option value="">Not selected</option><option value="ACORD Sent">ACORD Sent</option><option value="Client Cancel">Client Cancel</option></select></label>
          </div></details>
          <details className="quote-section"><summary>Follow-up</summary><div className="form-grid">
            <label className="field">Due date<input type="date" value={form.dueDate ?? ''} onChange={set('dueDate')} /></label>
            <label className="field">Renewal date<input type="date" value={form.renewalDate ?? ''} onChange={set('renewalDate')} /></label>
            <label className="field full">Needs<textarea value={form.needs ?? ''} onChange={set('needs')} /></label>
          </div></details>
        </fieldset>
        {canEdit && (
          <div className="actions">
            {dirty && <button type="button" className="btn" onClick={() => setForm(pick(o))}>Discard changes</button>}
            <span role="status" aria-live="polite">{saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' && !dirty ? 'Saved' : saveStatus === 'error' ? 'Save error — retry manually' : dirty ? 'Unsaved changes' : 'Saved'}</span>
            <button className="btn primary" type="submit" disabled={busy || !dirty}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        )}
      </form>

      {(canEdit || a.manager) && (
        <div className="actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="btn small danger" disabled={busy}
            onClick={() => {
              if (!window.confirm(`Archive ${o.customerName}? It leaves every list and report. The agency owner or office manager can restore it from the Archive.`)) return;
              void act('Archive failed', () => api(a.path(`/opportunities/${o.opportunityId}/archive`), { method: 'POST' })).then(() => nav(`${a.base}/quotes`));
            }}>Archive quote</button>
        </div>
      )}

      </div>}
      {tab === 'documents' && <div style={{ marginTop: 16 }}>
        <DocumentsPanel path={`/opportunities/${o.opportunityId}/documents`} onChange={q.reload} readOnlyNote="Read-only: only people who can edit this quote can add documents." />
      </div>}

      {tab === 'activity' && <div className="grid2" style={{ marginTop: 16 }}>
        <section className="card" aria-labelledby="activity">
          <h2 id="activity">Activity</h2>
          <form className="form-row" onSubmit={(e) => { e.preventDefault(); if (busy || !note.trim()) return; void act('Note failed', () => api(a.path(`/opportunities/${o.opportunityId}/notes`), { method: 'POST', body: { text: note, mentionMembershipIds: mentionIds } }), 'Message posted.').then(() => { setNote(''); setMentionIds([]); setMentionSearch(''); mentions.reload(); }); }}>
            <label className="field">Message<input value={note} maxLength={4000} onChange={(e) => setNote(e.target.value)} placeholder="Update for the team…" /></label>
            <label className="field">Tag coworker (@mention)
              <input value={mentionSearch} onChange={(e) => setMentionSearch(e.target.value)} placeholder="Search active staff…" aria-label="Search coworkers to mention" />
            </label>
            {mentionSearch.trim() && <div className="actions" aria-label="Matching coworkers">
              {activeStaff.filter((m) => !mentionIds.includes(m.membershipId) && `${m.displayName ?? ''} ${m.email}`.toLowerCase().includes(mentionSearch.toLowerCase())).slice(0, 8).map((m) =>
                <button key={m.membershipId} className="btn" type="button" onClick={() => { setMentionIds((ids) => [...ids, m.membershipId]); setMentionSearch(''); setNote((v) => `${v.trimEnd()} @${m.displayName ?? m.email} `); }}>@{m.displayName ?? m.email}</button>)}
            </div>}
            {!!mentionIds.length && <div className="actions" aria-label="Tagged coworkers">
              {mentionIds.map((id) => <button key={id} className="btn" type="button" onClick={() => setMentionIds((ids) => ids.filter((x) => x !== id))}>@{a.name(id)} ×</button>)}
            </div>}
            <button className="btn" type="submit" disabled={busy || !note.trim()}>Post message</button>
          </form>
          {!!noteMentions.length && <div className="card" aria-label="Mentions to address">
            <h3>You're mentioned in this quote</h3>
            {noteMentions.map((m) => <div key={m.mentionId} className="actions">
              <span className="muted">Mentioned by {a.name(m.authorMembershipId)} · {dateTime(m.createdAt)}</span>
              <button type="button" className="btn" disabled={busy} onClick={() => void act('Could not mark mention addressed', () => api(a.path(`/mentions/${m.mentionId}/address`), { method: 'POST', body: {} }), 'Mention addressed.').then(() => mentions.reload())}>Mark addressed</button>
            </div>)}
          </div>}
          <ul className="timeline" style={{ marginTop: 10 }}>
            {activity.map((x, i) => <li key={i}><div>{x.body}</div><div className="when">{x.authorMembershipId ? a.name(x.authorMembershipId) : 'ACC workflow'} · {dateTime(x.createdAt)}</div></li>)}
            {!activity.length && <li className="muted">No activity yet.</li>}
          </ul>
        </section>
        <section className="card" aria-labelledby="history">
          <h2 id="history">History</h2>
          <ul className="timeline">
            {events.map((e, i) => <li key={i}><div>{e.coverage === 'PC' ? 'P/C' : 'Life'} {e.type.toLowerCase()}{e.ownerMembershipId ? ` · owner ${a.name(e.ownerMembershipId)}` : ''}</div><div className="when">{dateTime(e.occurredAt)}</div></li>)}
            {assignments.map((s, i) => <li key={`a${i}`}><div>Assigned to {a.name(s.membershipId)}</div><div className="when">{dateTime(s.startedAt)} – {s.endedAt ? dateTime(s.endedAt) : 'now'}</div></li>)}
            {!events.length && !assignments.length && <li className="muted">No history yet.</li>}
          </ul>
        </section>
      </div>}
      </section>
      </div>
    </>
  );
}
